import * as React from 'react'

import styled from '@theme/styled-components'
import Theme from '@theme/Theme'
import useI18n from '@store/i18n/useI18n'

import Searchbar from '@components/searchbar/searchbar'
import InfiniteList from '@components/infiniteList/infiniteList'
import Chantier from '@components/listItem/business'
import Loader from 'react-loader-spinner'

interface Props {
  businesses: BusinessResume[]
  selected?: BusinessResume
  setSelected: (b?: BusinessResume) => void
  placeholder: string
  businessStatus: ScreenStatus
  text: string
  setText: (t: string) => void
  isDisabled?: boolean
  isClearable?: boolean
  bordered?: boolean
}

const BusinessesSelect = ({
  businesses,
  selected,
  setSelected,
  placeholder,
  businessStatus,
  text,
  setText,
  isDisabled,
  isClearable = true,
  bordered,
}: Props) => {
  const i18n = useI18n()

  const [selectOpen, setSelectOpen] = React.useState<boolean>(false)

  return (
    <>
      <Container>
        <SelectContainer
          selectOpen={selectOpen}
          bordered={bordered}
          isDisabled={isDisabled}
          onClick={() => !isDisabled && setSelectOpen(true)}>
          {businessStatus === 'loading' ? (
            <StatusContainer>
              <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={10} />
            </StatusContainer>
          ) : businessStatus === 'error' ? (
            <StatusContainer>{i18n.t('components.detailsChantier.error')}</StatusContainer>
          ) : selected ? (
            selected.title
          ) : (
            placeholder
          )}
          {!!selected && !!isClearable && (
            <DeleteSelected
              height="20"
              width="20"
              viewBox="0 0 20 20"
              fill="#0053A1"
              onClick={() => setSelected(undefined)}>
              <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
            </DeleteSelected>
          )}
        </SelectContainer>
      </Container>
      {selectOpen && (
        <Background>
          <SelectorContent onClick={() => null}>
            <ScreenTitleContainer>
              <ScreenTitle>{placeholder}</ScreenTitle>
              <Back onClick={() => setSelectOpen(false)}>{i18n.t('components.menu.back')}</Back>
            </ScreenTitleContainer>
            <OptionList>
              <SarchContainer>
                <Searchbar placeholder={i18n.t('components.menu.searchbar')} text={text} setText={(e) => setText(e)} />
              </SarchContainer>
              {businessStatus === 'loading' ? (
                <StatusContainer>
                  <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={10} />
                </StatusContainer>
              ) : (
                <>
                  {businesses.length === 0 ? (
                    <NoOption>{i18n.t('components.menu.noChantier')}</NoOption>
                  ) : (
                    <InfiniteList
                      data={businesses}
                      height={100}
                      renderItem={(business) => (
                        <Chantier
                          height={100}
                          chantier={business}
                          onClick={() => {
                            setSelected(business)
                            setSelectOpen(false)
                          }}
                        />
                      )}
                    />
                  )}
                </>
              )}
            </OptionList>
          </SelectorContent>
        </Background>
      )}
    </>
  )
}

export default BusinessesSelect

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin: 5px 20px;
`
const SelectContainer = styled('div')<{ selectOpen: boolean; bordered?: boolean; isDisabled?: boolean }>`
  border: ${(props) => (props.bordered ? '2px solid red' : '1px solid #919191')};
  ${(props) => (props.isDisabled ? 'cursor: not-allowed;' : 'cursor: pointer;')}
  background: ${(props) => (props.isDisabled ? '#91919155' : '#FFFFFF')};
  ${(props) => props.theme.fonts.selectLibelle}
  box-sizing: border-box;
  align-items: center;
  padding: 0 10px;
  border-radius: 7px;
  min-height: 50px;
  display: flex;
  justify-content: space-between;
`
const Background = styled('div')`
  background-color: ${(props) => props.theme.colors.backgroundColor};
  position: absolute;
  cursor: pointer;
  z-index: 1;
  bottom: 0;
  right: 0;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  color: red;
  cursor: pointer;
  font-weight: bold;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  padding: 15px;

  display: flex;
  align-items: center;
  justify-content: space-between;
`
const SelectorContent = styled('div')`
  flex: 1;
  z-index: 2;
  display: flex;
  height: 580px;
  padding: 10px;
  max-width: 730px;
  border-radius: 20px;
  flex-direction: column;
  background-color: white;
  box-shadow: 0px 0px 15px ${(props) => props.theme.colors.menuShadow};
`
const OptionList = styled('div')`
  flex: 1;
  display: flex;
  overflow: scroll;
  flex-direction: column;
`
const NoOption = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  justify-content: center;
  align-items: center;
  text-align: center;
  display: flex;
  flex: 1;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  font-size: 13px;
`
const SarchContainer = styled('div')`
  padding: 10px 15px;
`
const DeleteSelected = styled('svg')`
  cursor: pointer;
`
